import { api } from '@/lib/api'
import { PROFILE_PHOTO_EVENT } from '../shellEvents'
import {
  CHAT_ROOM_TYPES,
  RPC_MAX_ROOM_MEMBERS,
  RPC_MAX_SEEN_MEMBERS,
  RPC_TIMEOUT_MS,
  type RpcEvent,
  type RpcFieldError,
  type RpcMethod,
  type RpcResult,
} from './contract'

/* ------------------------------------------------------------------
   جدولِ ثابتِ متدهای RPC — سنجشِ ورودی و نگاشت به REST
   ------------------------------------------------------------------
   🔴 چرا هر متد قاعدهٔ خودش را دارد و ورودی «پاک» ساخته می‌شود.

   `params` از قابِ دامنهٔ دیگر می‌آید. اگر همان شیء به بدنهٔ درخواست
   می‌رفت، هر کلیدی که سرور امروز یا فردا بپذیرد از قاب قابلِ فرستادن
   بود. پس از هر متد فقط کلیدهای نام‌برده، با نوع و اندازهٔ درست، در
   شیءِ تازه‌ای کپی می‌شوند؛ کلیدِ ناشناخته `bad_params` است.

   ⚠️ مسیرها فقط از ثابت‌ها و شناسهٔ سنجیده ساخته می‌شوند — هیچ تکه‌ای
   از نشانی، فعل یا سرآیند از قاب نمی‌آید. این سنجش جای مجوزِ سرور را
   نمی‌گیرد؛ سرور همه را دوباره می‌سنجد.
   ------------------------------------------------------------------ */

/** شناسهٔ اتاق یا کاربرِ Rocket.Chat — همان الگوی مسیرِ سرور (`{rid}`). */
const CHAT_ID = /^[A-Za-z0-9]{1,64}$/
/** یک لحظهٔ کامل با منطقه — `ts` ِ پیامی که چت دارد. */
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:?\d{2})$/
/** نامِ کاربریِ گفتگو — بی فاصله و بی نویسهٔ کنترلی. */
const CHAT_USERNAME = /^[^\s\p{Cc}@#,]{1,191}$/u

export class RpcParamError extends Error {
  readonly fields: RpcFieldError[]

  constructor(fields: RpcFieldError[]) {
    super('ورودیِ درخواست درست نیست.')
    this.name = 'RpcParamError'
    this.fields = fields
  }
}

/* ------------------------------------------------------------------
   قاعده‌ها
   ------------------------------------------------------------------ */

/**
 * `null`:
 *   · `keep`   یعنی «پاک کن» و به سرور می‌رود؛
 *   · `drop`   همان «نیامدن» (فیلدِ پاک‌نشدنی)؛
 *   · `reject` نوعِ نادرست.
 */
type NullRule = 'keep' | 'drop' | 'reject'

type Rule =
  | { kind: 'string'; max: number; min?: number; required?: boolean; onNull?: NullRule }
  | { kind: 'enum'; values: readonly string[]; required?: boolean; onNull?: NullRule }
  | { kind: 'chatId'; required?: boolean; onNull?: NullRule }
  | { kind: 'chatIds'; max: number; onNull?: NullRule }
  | { kind: 'usernames'; max: number; onNull?: NullRule }
  | { kind: 'instant'; onNull?: NullRule }

type Rules = Record<string, Rule>

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

function check(value: unknown, rule: Rule, field: string, errors: RpcFieldError[]): unknown {
  const fail = (code: string) => {
    errors.push({ field, code })
    return undefined
  }

  switch (rule.kind) {
    case 'string': {
      if (typeof value !== 'string') return fail('type')
      const trimmed = value.trim()
      if (rule.min !== undefined && trimmed.length < rule.min) return fail(trimmed === '' ? 'required' : 'min')
      if (trimmed.length > rule.max) return fail('max')
      return value
    }
    case 'enum':
      if (typeof value !== 'string') return fail('type')
      return rule.values.includes(value) ? value : fail('in')
    case 'chatId':
      if (typeof value !== 'string') return fail('type')
      return CHAT_ID.test(value) ? value : fail('format')
    case 'chatIds': {
      if (!Array.isArray(value)) return fail('type')
      const out: string[] = []
      value.forEach((item, i) => {
        if (typeof item !== 'string' || !CHAT_ID.test(item)) errors.push({ field: `${field}.${i}`, code: 'format' })
        else if (!out.includes(item)) out.push(item)
      })
      return out.length > rule.max ? fail('max') : out
    }
    case 'usernames': {
      if (!Array.isArray(value)) return fail('type')
      if (value.length > rule.max) return fail('max')
      const out: string[] = []
      value.forEach((item, i) => {
        const name = typeof item === 'string' ? item.trim() : null
        if (name === null || !CHAT_USERNAME.test(name)) errors.push({ field: `${field}.${i}`, code: 'format' })
        else if (!out.some((n) => n.toLowerCase() === name.toLowerCase())) out.push(name)
      })
      return out
    }
    case 'instant': {
      /* ⚠️ به ISO ِ UTC با میلی‌ثانیه برمی‌گردد — همان شکلی که سرور مقایسه می‌کند. */
      if (typeof value !== 'string') return fail('type')
      const at = INSTANT.test(value) ? Date.parse(value) : Number.NaN
      return Number.isNaN(at) ? fail('format') : new Date(at).toISOString()
    }
  }
}

/** ورودیِ پاک: فقط کلیدهای قاعده‌دار، سنجیده. */
export function parseParams(raw: unknown, rules: Rules): Record<string, unknown> {
  if (raw === undefined || raw === null) raw = {}
  if (!isPlainObject(raw)) throw new RpcParamError([{ field: 'params', code: 'type' }])

  const errors: RpcFieldError[] = []
  const out: Record<string, unknown> = {}

  for (const key of Object.keys(raw)) {
    if (raw[key] !== undefined && !Object.prototype.hasOwnProperty.call(rules, key)) {
      errors.push({ field: key, code: 'unknown' })
    }
  }

  for (const [key, rule] of Object.entries(rules)) {
    const value = Object.prototype.hasOwnProperty.call(raw, key) ? raw[key] : undefined
    const required = 'required' in rule && rule.required === true

    if (value === undefined) {
      if (required) errors.push({ field: key, code: 'required' })
      continue
    }

    if (value === null) {
      const nullRule = rule.onNull ?? 'reject'
      if (nullRule === 'keep' && !required) out[key] = null
      else if (nullRule === 'drop' && !required) continue
      else errors.push({ field: key, code: required ? 'required' : 'type' })
      continue
    }

    const clean = check(value, rule, key, errors)
    if (clean !== undefined) out[key] = clean
  }

  if (errors.length > 0) throw new RpcParamError(errors)
  return out
}

/* ------------------------------------------------------------------
   REST
   ------------------------------------------------------------------ */

type Query = Record<string, string | number | undefined>

/*
  ⚠️ خواندن پیش از پایانِ مهلتِ چت رها می‌شود: چت پس از `RPC_TIMEOUT_MS` خودش
  کنار می‌کشد و دوباره می‌پرسد، و درخواستِ قبلی (مهلتِ ۳۰ ثانیه‌ایِ `api`)
  جای خالیِ پل را نگه می‌داشت و درست وقتی سرور کُند است کارِ تکراری می‌ساخت.
  نوشتن رها نمی‌شود — گروهی که نیمه‌کاره رها شود ممکن است دو بار ساخته شود.
*/
async function get<T>(path: string, query?: Query): Promise<T> {
  const response = await api.get<{ data: T }>(path, { params: query, signal: AbortSignal.timeout(RPC_TIMEOUT_MS - 500) })
  return response.data.data
}

async function send<T>(method: 'post' | 'patch' | 'put' | 'delete', path: string, body?: object): Promise<T> {
  const response = await api.request<{ data: T }>({ method, url: path, data: body })
  return response.data.data
}

/** شناسه پیش از این سنجیده شده؛ `encodeURIComponent` فقط برای اطمینان. */
const at = (base: string, id: unknown, tail = '') => `${base}/${encodeURIComponent(String(id))}${tail}`

const text = (value: unknown) => (typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined)

/* ------------------------------------------------------------------
   جدول
   ------------------------------------------------------------------ */

interface MethodSpec<M extends RpcMethod> {
  rules: Rules
  call(params: Record<string, unknown>): Promise<RpcResult<M>>
  /** رویدادهای پس از موفقیت — فقط برای متدهای نویسنده. */
  events?(params: Record<string, unknown>, result: RpcResult<M>): RpcEvent[]
  /**
   * کارِ خودِ کاربر است، حتی بی رویداد (مثلاً ساختنِ گروه) — «آنلاین» ثبت می‌شود.
   * ⚠️ پرسش‌های زمان‌دارِ رابط (مثلِ `chat.seen` هر ۱۲ ثانیه) هرگز.
   */
  activity?: boolean
}

/* نام: در ساختن لازم، در ویرایش اختیاری — ولی هرگز خالی. */
const name = (max: number, required: boolean): Rule =>
  required ? { kind: 'string', max, min: 1, required } : { kind: 'string', max, min: 1, onNull: 'drop' }
export const RPC_TABLE: { [M in RpcMethod]: MethodSpec<M> } = {
  'me.context': {
    rules: {},
    call: () => get('/tasks/me'),
  },

  'colleagues.list': {
    rules: { search: { kind: 'string', max: 100, onNull: 'drop' } },
    call: (p) => get('/tasks/colleagues', { search: text(p.search) }),
  },

  /*
    گفتگو — زیرِ `/api/v1/chat`. ⚠️ `members` ِ «دیده شد» با ویرگول در نشانی می‌رود
    (حداکثر ۵۰ شناسهٔ ۶۴نویسه‌ای)، نه آرایه‌ای که axios به `members[]` می‌شکند.
  */
  'chat.seen': {
    rules: {
      rid: { kind: 'chatId', required: true },
      members: { kind: 'chatIds', max: RPC_MAX_SEEN_MEMBERS, onNull: 'drop' },
      after: { kind: 'instant', onNull: 'drop' },
    },
    call: (p) =>
      get(at('/chat/rooms', p.rid, '/seen'), {
        members: Array.isArray(p.members) && p.members.length > 0 ? p.members.join(',') : undefined,
        after: p.after as string | undefined,
      }),
  },

  'rooms.capabilities': {
    rules: {},
    call: () => get('/chat/rooms/capabilities'),
  },

  'rooms.create': {
    rules: {
      type: { kind: 'enum', values: CHAT_ROOM_TYPES, required: true },
      name: name(64, true),
      members: { kind: 'usernames', max: RPC_MAX_ROOM_MEMBERS, onNull: 'drop' },
      description: { kind: 'string', max: 500, onNull: 'keep' },
    },
    call: (p) => send('post', '/chat/rooms', p),
    activity: true,
  },

  /*
    عکسِ نمایه در گفتگو عوض شد — کاربن خودش از Rocket.Chat می‌خواند؛ از قاب فقط
    «حالا نگاه کن» می‌آید. ⚠️ پس از پاسخ، پوستهٔ پنل هم باخبر می‌شود
    (`PROFILE_PHOTO_EVENT`) تا عکسِ نوارِ بالا و فهرست‌ها همان لحظه عوض شوند.
  */
  'me.photoChanged': {
    rules: {},
    call: async () => {
      const result = await send<RpcResult<'me.photoChanged'>>('post', '/chat/me/photo')
      window.dispatchEvent(new Event(PROFILE_PHOTO_EVENT))
      return result
    },
    activity: true,
  },

  /*
    شمارهٔ موبایلِ خودم (۱۴۰۵/۰۸/۱۲) — زیرِ `/api/v1/me` ِ ماژولِ پرسنل؛ پاسخِ سرور snake_case است
    و این‌جا به همان شکلِ camelCase ِ قرارداد برمی‌گردد. ⚠️ رقم‌های فارسی را سرور خودش می‌فهمد.
  */
  'me.mobile.requestCode': {
    rules: { mobile: { kind: 'string', max: 32, min: 1, required: true } },
    call: async (p) => {
      const r = await send<{ mobile: string; expires_in: number; resend_in: number }>('post', '/me/mobile/request-code', { mobile: p.mobile })
      return { mobile: r.mobile, expiresIn: r.expires_in, resendIn: r.resend_in }
    },
    activity: true,
  },

  'me.mobile.verify': {
    rules: {
      mobile: { kind: 'string', max: 32, min: 1, required: true },
      code: { kind: 'string', max: 16, min: 1, required: true },
    },
    call: async (p) => {
      const r = await send<{ work_mobile: string }>('post', '/me/mobile/verify', { mobile: p.mobile, code: p.code })
      return { workMobile: r.work_mobile }
    },
    activity: true,
  },
}
